import { SlBarGraphData } from '@varo/graphql-schema-types';
import { scaleBand, scaleLinear } from 'd3-scale';
import { stack } from 'd3-shape';
import React, { useState } from 'react';
import { LayoutChangeEvent, StyleSheet, View } from 'react-native';
import Svg, { G, Line, Path, Rect, Text as SvgText } from 'react-native-svg';
import { handleLayoutEvent } from 'shared/components/SmartViews/Charts/layoutUtils';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';
import { getVXLColor } from 'shared/utils/getVXLColor/getVXLColor';

import { BAR_CHART_DIMENSIONS } from './BarChart.constants';

export type BarChartProps = {
  data: SlBarGraphData;
};

const BarChart = ({ data }: BarChartProps) => {
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });

  const bars = data.bars;
  const yScaleData = data.yScale;
  const {
    paddingHorizontal,
    bottomPadding,
    topPadding,
    yAxisLabelWidth,
    barWidth,
    topRadius,
  } = BAR_CHART_DIMENSIONS;

  const svgWidth =
    containerSize.width - paddingHorizontal * 2 - yAxisLabelWidth;
  const svgHeight = containerSize.height - topPadding - bottomPadding;
  const maxValue = yScaleData.maxValue;

  const segmentKeys = bars.reduce((keys, bar) => {
    bar.segments.forEach((_, i) => {
      const key = `segment${i}`;
      if (!keys.includes(key)) {
        keys.push(key);
      }
    });
    return keys;
  }, [] as string[]);

  const formattedData = bars.map((bar) =>
    segmentKeys.reduce(
      (acc, key, i) => {
        const segment = bar.segments[i];
        acc[key] = segment ? parseFloat(segment.amount.amount as string) : 0;
        return acc;
      },
      {} as Record<string, number>,
    ),
  );

  const stackedData = stack().keys(segmentKeys)(formattedData);

  const xScale = scaleBand<string>()
    .domain(bars.map((bar) => bar.title))
    .range([0, svgWidth])
    .padding(0.1);

  const yScale = scaleLinear().domain([0, maxValue]).range([svgHeight, 0]);

  const rulePositions: number[] = yScaleData.labels
    .slice(0, -1)
    .map((label, index) => {
      const nextLabel = yScaleData.labels[index + 1];
      return nextLabel
        ? (yScale(label.value) + yScale(nextLabel.value)) / 2
        : 0;
    });

  const maxSegmentValue = stackedData.reduce<number>((max, layer) => {
    const layerMax = layer.reduce<number>((layerMax, segment) => {
      return Math.max(layerMax, segment[1]);
    }, -Infinity);
    return Math.max(max, layerMax);
  }, -Infinity);
  const threshold = maxSegmentValue * 0.01;

  return (
    <View
      style={styles.container}
      onLayout={(event: LayoutChangeEvent) =>
        handleLayoutEvent(event, setContainerSize)
      }
    >
      <Svg
        accessibilityLabel="stacked-bar-chart"
        width={svgWidth + paddingHorizontal * 2 + yAxisLabelWidth}
        height={svgHeight + topPadding + bottomPadding}
      >
        {/* Horizontal rules */}
        {yScaleData.labels.map((label) => (
          <G key={`label-${label.value}`}>
            <Line
              x1={paddingHorizontal}
              y1={yScale(label.value) + topPadding}
              x2={svgWidth + paddingHorizontal}
              y2={yScale(label.value) + topPadding}
              stroke={theme.color.border.primary}
              strokeWidth={1}
              accessibilityLabel={`Horizontal-line-at-${label.value}`}
            />

            <SvgText
              x={svgWidth + paddingHorizontal + 10}
              y={yScale(label.value) + topPadding}
              textAnchor="start"
              dy="0.32em"
              fontSize={12}
              fill={theme.color.text.secondary}
              fontFamily={VaroFontNames.NeueHaasGroteskDisplayProRomanNormal}
              accessibilityLabel={label.title}
            >
              {label.title}
            </SvgText>
          </G>
        ))}

        {/* Lines between y-axis labels */}
        {rulePositions.map((position) => (
          <Line
            key={`rule-${position}`}
            x1={paddingHorizontal}
            y1={position + topPadding}
            x2={svgWidth + paddingHorizontal}
            y2={position + topPadding}
            stroke={theme.color.border.primary}
            strokeWidth={1}
          />
        ))}

        <G transform={`translate(${paddingHorizontal},${topPadding})`}>
          {stackedData.map((layer, layerIndex) => {
            const topSegments = bars.map((_bar, barIndex) => {
              const segments = stackedData.map((layer) => layer[barIndex]);
              const topSegment = segments.reduce(
                (max, segment) => (segment[1] > max[1] ? segment : max),
                segments[0],
              );
              return topSegment;
            });
            const layerKey = `layer-${layerIndex}`;
            return (
              <G key={layerKey}>
                {layer.map((d, i) => {
                  const bar = bars[i];
                  const xPosition =
                    xScale(bar.title)! + (xScale.bandwidth() - barWidth) / 2;
                  const yPosition = yScale(d[1]) + 2;
                  const barHeight = yScale(d[0]) - yScale(d[1]) - 2;

                  const isTopSegment =
                    JSON.stringify(d) === JSON.stringify(topSegments[i]);
                  const segmentHeight = yScale(d[0]) - yScale(d[1]);
                  const applyTopRadius = segmentHeight > threshold;

                  const pathData =
                    isTopSegment && applyTopRadius
                      ? `
            M ${xPosition},${yPosition + topRadius}
            a ${topRadius},${topRadius} 0 0 1 ${topRadius},${-topRadius}
            h ${barWidth - 2 * topRadius}
            a ${topRadius},${topRadius} 0 0 1 ${topRadius},${topRadius}
            v ${barHeight - topRadius}
            h ${-barWidth}
            v ${-barHeight + topRadius}
            Z
          `
                      : undefined;

                  const color =
                    bar?.segments?.[layerIndex]?.color &&
                    getVXLColor(bar?.segments?.[layerIndex]?.color);

                  if (color) {
                    return pathData ? (
                      <Path
                        key={`${bar.title}-${d[1]}`}
                        d={pathData}
                        fill={color}
                        accessibilityLabel={`${bar.title}-bar`}
                      />
                    ) : (
                      <Rect
                        key={`${bar.title}-${color}-${d[1]}`}
                        x={xPosition}
                        y={yPosition}
                        width={barWidth}
                        height={barHeight < 0 ? 0 : barHeight}
                        fill={color}
                        accessibilityLabel={`${bar.title}-${color}-bar`}
                      />
                    );
                  }
                })}
              </G>
            );
          })}

          {bars.map((bar) => (
            <SvgText
              key={`title-${bar.title}`}
              x={xScale(bar.title)! + xScale.bandwidth() / 2}
              y={svgHeight + 20}
              fontSize={12}
              textAnchor="middle"
              fill={theme.color.text.secondary}
              fontFamily={VaroFontNames.NeueHaasGroteskDisplayProRomanNormal}
              accessibilityLabel={`Label-for-${bar.title}`}
            >
              {bar.title}
            </SvgText>
          ))}
        </G>
      </Svg>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    height: 250,
    justifyContent: 'center',
    width: '100%',
  },
});

export default BarChart;
